import type { DemographicConfiguration } from "../../../types/demographic.types";
import { ethnicityTypes } from "@/enum/ethnicityType";

// Same list, same order as the patient add/update modals. Values are the
// ethnicity labels so an amendment maps straight back to the numeric
// patient-record key, and the ethnic-adjustment group is derived from the
// label by mapEthnicityToAdjustmentGroup.
const ethnicityOptions = Object.entries(
  ethnicityTypes as Record<string, string>,
).map(([, label]) => ({ value: label, label }));

export const AHT_SENSITIVE_ATTRIBUTE_CONSENT_BASIS = "healthcare_provision_art9_2h";

export const AHT_BIOLOGICAL_SEX_PURPOSE =
  "AHT weight management — IOTF cut-off lookup for adolescent eligibility and clinical decision-making";

export const AHT_ETHNIC_ADJUSTMENT_PURPOSE =
  "AHT weight management — ethnic-adjustment BMI threshold application for adult eligibility";

export const ahtDemographicConfiguration: DemographicConfiguration = {
  bannerFields: [
    { field: "dateOfBirth", label: "Date of birth", type: "date", changeLinkLabel: "Change date of birth" },
    {
      field: "ageYears",
      label: "Age (years)",
      type: "number",
      derivedFromDateOfBirth: { note: "from date of birth" },
    },
    {
      field: "biologicalSex",
      label: "Biological sex",
      type: "sensitive_attribute",
      selectOptions: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "other", label: "Other / prefer not to say" },
      ],
      helpText: "Required for IOTF adolescent BMI cut-off lookup",
      changeLinkLabel: "Change biological sex",
      whyWeAsk: "Why we ask: adolescent BMI cut-offs depend on sex (IOTF).",
      whyWeAskShowWhen: { patientAgeUnder: 18 },
      sensitiveAttribute: {
        consentBasis: AHT_SENSITIVE_ATTRIBUTE_CONSENT_BASIS,
        purposeOfProcessing: AHT_BIOLOGICAL_SEX_PURPOSE,
      },
    },
    {
      field: "ethnicAdjustmentGroup",
      label: "Ethnicity (for BMI thresholds)",
      type: "sensitive_attribute",
      selectOptions: ethnicityOptions,
      helpText:
        "Used to apply ethnic-adjustment BMI thresholds for adult eligibility",
      changeLinkLabel: "Change ethnicity",
      whyWeAsk: "Why we ask: lower BMI thresholds apply for some ethnic groups (NICE).",
      sensitiveAttribute: {
        consentBasis: AHT_SENSITIVE_ATTRIBUTE_CONSENT_BASIS,
        purposeOfProcessing: AHT_ETHNIC_ADJUSTMENT_PURPOSE,
      },
    },
  ],
};
